import _ from 'lodash';
import { Task } from 'redux-saga';
import { call, cancelled, fork, put, select, StrictEffect, take, takeEvery } from 'redux-saga/effects';
import { TAction } from '../../../../../../shared/frontend/scripts/actions/types/action';
import { IGetStatisticsDataQuery, TGetStatisticsData } from '../../../../../../shared/types/statisticsTypes';
import { generateStatisticsDataKey } from '../../../../shared/statisticsUtils';
import { TStatisticChartState } from '../../libs/store';
import { apiServiceAsync } from '../../services/apiService';
import { actions, actionTypes } from './modules';
import { statisticsStateSelector } from './selectors';

function* fetchStatisticsData(action: TAction<IGetStatisticsDataQuery>): any {
  const key = generateStatisticsDataKey(action.payload);

  try {
    yield put(
      actions.updateStatisticsData(key, {
        isLoading: true,
        ui: action.payload
      })
    );

    const data: TGetStatisticsData = yield call(apiServiceAsync.getStatisticsData, action.payload);

    const stats: Partial<Record<string, TStatisticChartState>> = yield select(statisticsStateSelector);
    const oldData = stats[key]?.data;

    const isSameData = oldData && data && _.isEqual(oldData, data);

    yield put(
      actions.updateStatisticsData(key, {
        ...(isSameData ? {} : { data }),
        isLoading: false,
        isLoaded: true
      })
    );
  } catch (error) {
    console.error('Error in fetchStatisticsData', error);

    if (error.message === 'Invalid version') {
      window.location.reload();
      return;
    }

    yield put(
      actions.updateStatisticsData(key, {
        isLoading: false,
        isLoaded: true,
        error: error.message || 'Something went wrong',
        data: undefined
      })
    );
  }
}

function* fetchStatisticsDataById(action: TAction<string>): any {
  const key = action.payload.toString();
  try {
    yield put(actions.updateStatisticsData(key, { isLoading: true }));
    const data: TGetStatisticsData = yield call(apiServiceAsync.getStatisticsDataById, action.payload);
    yield put(actions.updateStatisticsData(key, { isLoading: false, isLoaded: true, data }));
  } catch (error) {
    console.error('Error in fetchStatisticsDataById', error);
    yield put(
      actions.updateStatisticsData(key, {
        isLoading: false,
        isLoaded: true,
        error: error.message || 'Something went wrong',
        data: undefined
      })
    );
  }
}

function* watchRequestFetchStatisticsDataLeadingByKey(): any {
  yield takeLeadingPerKey(
    actionTypes.FETCH_STATISTICS_DATA,
    (action: TAction<IGetStatisticsDataQuery>) => generateStatisticsDataKey(action.payload),
    fetchStatisticsData
  );
}

function* watchRequestFetchStatisticsDataById(): any {
  yield takeEvery(actionTypes.FETCH_STATISTICS_DATA_BY_ID, fetchStatisticsDataById);
}

export default [watchRequestFetchStatisticsDataLeadingByKey(), watchRequestFetchStatisticsDataById()];

/**
 * takeLeadingPerKey - runs only one saga per unique key until it completes
 * @param pattern - action type
 * @param keySelector - function that returns a key from action
 * @param saga - saga function
 */
export function* takeLeadingPerKey<T extends { type: string }>(
  pattern: string,
  keySelector: (action: T) => string,
  saga: (action: T) => Generator<StrictEffect, any, any>
) {
  const tasks: Record<string, Task> = {};
  while (true) {
    const action: T = yield take(pattern);
    const key = keySelector(action);
    if (!tasks[key] || !tasks[key].isRunning()) {
      tasks[key] = yield fork(function* () {
        try {
          yield call(saga, action);
        } finally {
          if (yield cancelled()) {
            delete tasks[key];
          }
        }
      });
    }
  }
}
